import * as React from "react";
import { graphql } from "gatsby";
import { Layout } from '../components/layout/layout';
import { DestinationPageHero } from '../components/destination/hero';
import { Region, RegionMonth, RegionBase } from './fragments/region'
import firstCapital from '../services/firstCapitalCase'
import textParser from "../services/textParser";
import { monthSlugsWithOverview } from '../services/centralStore'
import { useLoc } from "../hooks/useLoc";
import { DestinationCards } from "../components/shared/cards";
import { TextAnswer, TextQuestion, TextWrapper } from "../components/destination/texts";
import { StatCards } from "../components/destination/statcards";
import { Gallery } from "../components/destination/gallery";
import { ChartWrapper } from "../components/destination/chart-wrapper";
import { SunheronFeatures } from "../components/shared/sh-features";
import { Resort } from "./fragments/resort";
import { Faq } from "../components/destination/faq";
import { WeatherSearchForm } from "../components/search/forms/weather-search";
import { useDelayedWeatherSearchForm } from "../hooks/useDelayedWeatherSearchForm";
import { LocationType } from "../interfaces";
import { ShareContent } from "../components/layout/share";

type RegionType = {
    data: Query,
    location: LocationType,
    pageContext: { month: string, slug: string, lang?: string, fullSlug: string }
}

const RegionPage = ({ data, location, pageContext }: RegionType) => {

    const loc = useLoc(pageContext.lang || 'en');
    let weatherOrClimate = pageContext.month == 'climate' ? loc('common.climate') : loc('common.weather')

    const faqs = data.regionsMonthsJson.questions.map((question: string, idx: number) => {
        return { question: question, answer: data.regionsMonthsJson.answers[idx] };
    });
    faqs.splice(0, 0, { question: firstCapital(weatherOrClimate) + ' ' + loc('common.overview'), answer: data.regionsMonthsJson.header })
    let header = `${data.regionsJson.name} ${weatherOrClimate}`
    header += pageContext.month != 'climate' ? ` ${loc('common.in')} ${loc('common.months.' + pageContext.month)}` : ''
    const destinationInfoHeader = firstCapital(weatherOrClimate) + ' ' + loc('common.overviewAndFaqs')

    return (<Layout path={"/" + pageContext.fullSlug} month={pageContext.month} title={header} faqs={faqs} description={textParser({ text: data.regionsMonthsJson.header, units: 'C', noNbsp: true })}>
        <DestinationPageHero
            className="lg:hidden"
            header={header}
            month={pageContext.month}
            bgImage={data.regionsJson.heroImgFull.childImageSharp.gatsbyImageData}
            region={data.regionsJson}
        />
        <DestinationPageHero
            className="lgmax:hidden"
            header={header}
            month={pageContext.month}
            bgImage={data.regionsJson.heroImg.childImageSharp.gatsbyImageData}
            region={data.regionsJson}
        />


        <RegionPageCoreContent {...{ pageContext }} month={pageContext.month} locationJson={data.regionsJson} />

        <div className="h-16"></div>

        <h2 className="m-auto w-destination-header font-destination-header mb-12">
            {`${loc('common.destinationsIn')} ${data.regionsJson.name}`}
        </h2>
        <DestinationCards regionsOrResorts={data.regionsJson.resorts} isResorts={true} className="" />

        <div className="h-16"></div>

        <h2 className="m-auto w-destination-header font-destination-header mb-12">
            {`${loc('common.otherDestinationsWithSimilar')} ${data.regionsJson.name}`}
        </h2>
        <DestinationCards regionsOrResorts={data.regionsMonthsJson.similar} className="" />

        <hr className="w-full xm:w-3/4 mt-16 mb-12 m-auto" />

        <h2 className="m-auto w-destination-header font-destination-header mt-16">
            {destinationInfoHeader}
        </h2>
        <Faq faqs={faqs} />
        <div className="h-20"></div>
        <ShareContent active={true} partOfPage={true} />




    </Layout >)
}


export const RegionPageCoreContent = ({ pageContext, locationJson, month }: {
    pageContext: { slug: string, lang?: string },
    locationJson: Region | Resort,
    month: string


}) => {
    const weatherData = locationJson.weatherData;
    const loc = useLoc(pageContext.lang || 'en');
    const monthIndex = monthSlugsWithOverview.indexOf(month);
    const delayedRenderId = useDelayedWeatherSearchForm({ scrollOffset: 600 });
    return (<><div className="h-12"></div>
        {month != 'climate' && <StatCards weatherData={weatherData} monthIndex={monthIndex}></StatCards>}
        <div className="h-10"></div>
        <h2 className="m-auto w-destination-header font-destination-header mb-10">
            {`${loc('common.weatherChartFor')} ${locationJson.name}`}
        </h2>
        <ChartWrapper
            weatherData={locationJson.weatherData}
        />
        <div className="h-6"></div>
        <SunheronFeatures />
        <div className="h-10"></div>

        <Gallery imgAlts={locationJson.imgAlts} galleryPhotos={locationJson.imgs.map(i => i.childImageSharp.gatsbyImageData)}></Gallery>
        <div className="h-16"></div>
        <h2 className="m-auto w-destination-header font-destination-header mb-10">
            {`Find more destinations like this`}
        </h2>
        <WeatherSearchForm delayedRenderId={delayedRenderId} />
    </>
    )
}



interface Query {
    regionsJson: Region,
    regionsMonthsJson: RegionMonth
}





export const query = graphql`

query QueryRegion($monthShort: String, $slug: String) {
    regionsJson(slug: { eq: $slug }) {
        ...RegionFull
        
    }
    regionsMonthsJson(month: { eq: $monthShort }, slug: { eq: $slug }) {
        ...RegionMonth
    }
}
`


export default RegionPage;